import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** Ardelle & Co: index of the photo on screen, and the state of the slide between photos. */
  #currentIndex = 0;
  #sliding = false;
  /** @type {number | null} */
  #pendingIndex = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#addThumbnailArrows();
    // a swipe on a photo (sent by drag-zoom-wrapper when the photo is not zoomed in) goes to the next / previous photo
    this.addEventListener('ac-photo-swipe', (event) => {
      const last = this.refs.media.length - 1;
      const next = Math.min(last, Math.max(0, this.#currentIndex + /** @type {CustomEvent} */ (event).detail.dir));
      if (next !== this.#currentIndex) this.selectThumbnail(next, { behavior: 'smooth' });
    });
  }

  /**
   * Ardelle & Co: slides to another photo as a transform animation on the two photos involved - the new one
   * comes in from the right when going forward and from the left when going back. Nothing is scrolled through, so
   * the photos in between never flash past, and only one slide is ever in flight (taps during a slide are queued).
   * @param {number} index
   */
  async #slidePhotoTo(index) {
    const { media } = this.refs;
    const target = media[index];
    if (!target) return;
    if (this.#sliding) {
      this.#pendingIndex = index;
      return;
    }
    const list = target.parentElement;
    const from = media[this.#currentIndex];
    if (!list || !from || from === target || !from.animate || prefersReducedMotion() || !list.clientWidth) {
      target.scrollIntoView({ behavior: 'instant' });
      this.#currentIndex = index;
      return;
    }

    this.#sliding = true;
    const snapType = list.style.scrollSnapType;
    try {
      // have the incoming photo loaded and drawn before it slides in (capped, so a slow connection never stalls it)
      const incoming = target.querySelector('img');
      if (incoming && !(incoming.complete && incoming.naturalWidth)) {
        await Promise.race([
          incoming.decode ? incoming.decode().catch(() => {}) : Promise.resolve(),
          new Promise((resolve) => setTimeout(resolve, 600)),
        ]);
      }
      const forward = index > this.#currentIndex;
      list.style.scrollSnapType = 'none';
      target.scrollIntoView({ behavior: 'instant' });
      this.#currentIndex = index;
      const box = list.getBoundingClientRect();
      const width = box.width;
      const dir = forward ? 1 : -1;
      const fromLeft = from.getBoundingClientRect().left - box.left;
      const toLeft = target.getBoundingClientRect().left - box.left;
      const timing = { duration: 360, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' };
      const leaving = from.animate(
        [{ transform: `translateX(${-fromLeft}px)` }, { transform: `translateX(${-fromLeft - dir * width}px)` }],
        timing
      );
      const arriving = target.animate(
        [{ transform: `translateX(${dir * width - toLeft}px)` }, { transform: 'translateX(0)' }],
        timing
      );
      await Promise.all([leaving.finished, arriving.finished]).catch(() => {});
      target.scrollIntoView({ behavior: 'instant' });
    } finally {
      list.style.scrollSnapType = snapType;
      this.#sliding = false;
    }

    // keep the next / previous photos' full-size versions coming, then play any tap that arrived meanwhile
    for (const near of [index - 1, index + 1]) if (media[near]) this.loadHighResolutionImage(media[near]);
    const pending = this.#pendingIndex;
    this.#pendingIndex = null;
    if (pending !== null && pending !== this.#currentIndex) this.#slidePhotoTo(pending);
  }

  /**
   * Ardelle & Co: up / down arrows on the vertical thumbnail rail (phones) that scroll the thumbnails.
   */
  #addThumbnailArrows() {
    const container = this.querySelector('.dialog-thumbnails-list-container');
    const list = this.refs.thumbnails;
    if (!container || !list || container.querySelector('.ac-thumb-arrow')) return;
    // Single-photo products have no thumbnails, so no rail arrows.
    if (list.querySelectorAll('.dialog-thumbnails-list__thumbnail').length < 2) return;

    const chevron = (up) =>
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="' +
      (up ? '6 15 12 9 18 15' : '6 9 12 15 18 9') +
      '"></polyline></svg>';

    const thumbs = () => Array.from(list.children).filter((el) => el instanceof HTMLElement);
    const currentIndex = () => Math.max(0, thumbs().findIndex((t) => t.getAttribute('aria-selected') === 'true'));

    // The up / down arrows switch to the previous / next photo (and keep its thumbnail in view).
    const buttons = [];
    const sync = () => {
      const i = currentIndex();
      const last = thumbs().length - 1;
      for (const btn of buttons) {
        const disabled = btn.classList.contains('ac-thumb-arrow--up') ? i <= 0 : i >= last;
        btn.disabled = disabled;
        btn.classList.toggle('is-disabled', disabled);
      }
    };

    for (const up of [true, false]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ac-thumb-arrow ' + (up ? 'ac-thumb-arrow--up' : 'ac-thumb-arrow--down');
      button.setAttribute('aria-label', up ? 'Previous photo' : 'Next photo');
      button.innerHTML = chevron(up);
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const next = Math.min(thumbs().length - 1, Math.max(0, currentIndex() + (up ? -1 : 1)));
        this.selectThumbnail(next, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
        requestAnimationFrame(sync);
      });
      buttons.push(button);
      container.appendChild(button);
    }

    new MutationObserver(sync).observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
    sync();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#currentIndex = index;
    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    // Ardelle & Co: the full-screen frosted-glass viewer is too heavy for the photo-morph (view transition) -
    // it stuttered when closing. Open and close it directly.
    if (true || !supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      // Keep the thumbnail highlight on the photo that was opened (the view-transition path did this afterwards).
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    // Ardelle & Co: decode the full-size photo before swapping it in, so the swap never flashes blank.
    newImage.onload = async () => {
      try {
        if (newImage.decode) await newImage.decode();
      } catch (error) {
        /* swap anyway */
      }
      if (!image.isConnected) return;
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (this.#sliding) return;
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (this.#sliding) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex >= 0) this.#currentIndex = activeIndex;
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (true || !supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    // Ardelle & Co: don't leave focus (and its dark outline) on the photo that was tapped to open the viewer.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      if (options.behavior === 'smooth' && this.refs.dialog.open) {
        this.#slidePhotoTo(index);
      } else {
        targetImage.scrollIntoView({
          behavior: 'instant',
        });
        this.#currentIndex = index;
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
